import Link from 'next/link'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Container } from '~/components/ui/Container'
import QRCode from 'qrcode.react'
import { Popover, Tooltip } from 'antd'
import { observer } from 'mobx-react'
import { useStores } from '~/hooks/useStores'
import { ThemeType } from '~/graphqls/types'
import { MyIcon } from '~/components/ui/MyIcon'
import { AiSkuTypeAgreement } from '~/configs/index'
import { SkuType } from '~/graphqls/types'

const StyledFooter = styled.div<{ isDarkMode: boolean }>`
  margin-top: 40px;
  border-top: 1px solid
    ${props => (props.isDarkMode ? props.theme.dark3 : props.theme.light1)};
  .footer-main {
    padding: 40px 0;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid
      ${props => (props.isDarkMode ? props.theme.dark3 : props.theme.light1)};
  }
  .footer-logo {
    width: 280px;
    line-height: 24px;
    font-size: 16px;
    color: ${props =>
      props.isDarkMode ? props.theme.light1 : props.theme.dark2};
    img {
      display: block;
      height: 40px;
      margin-bottom: 32px;
    }
  }
  .link-item-title {
    font-weight: 500;
    line-height: 24px;
    font-size: 16px;
    color: ${props =>
      props.isDarkMode ? props.theme.light1 : props.theme.dark2};
    margin-bottom: 40px;
  }
  .qrCode {
    border: 1px solid
      ${props => (props.isDarkMode ? props.theme.dark3 : props.theme.light1)};
    padding: 2px;
    width: 96px;
    height: 96px;
  }
`

const StyledLink = styled.div`
  display: flex;
`

const StyledLinkItem = styled.div`
  width: 160px;
  & + & {
    margin-left: 32px;
  }
`

const StyledLinkItemLink = styled.div`
  /* font-weight: 600; */
  line-height: 16px;
  font-size: 14px;
  color: ${props => props.theme.dark4};
  & + & {
    margin-top: 24px;
  }
`

const StyledCopyright = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px 0;
`

const StyledCopyrightText = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${props => props.theme.dark4};
`

const StyledSocial = styled.div`
  display: flex;
`

const StyledSocialItem = styled.div`
  width: 24px;
  height: 24px;
  cursor: pointer;

  & + & {
    margin-left: 16px;
  }
`

export const Footer: React.FC = observer(() => {
  const { store } = useStores()

  const isDarkMode = useMemo(() => {
    return store.themeMode === ThemeType.DARK
  }, [store])

  return (
    <StyledFooter isDarkMode={isDarkMode} className="footer">
      <Container>
        <div className="footer-main">
          <div className="footer-logo">
            <img
              src={
                isDarkMode ? '/images/logo-v2-dark.svg' : '/images/logo-v2.svg'
              }
              alt=""
            />
            用区块链技术为艺术作品提供安全可靠易用的数字版权登记和拍卖平台
          </div>
          <StyledLink>
            <StyledLinkItem>
              <div className="link-item-title">关于我们</div>
              <StyledLinkItemLink>
                <Link href="/us/about">
                  <a>关于我们</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href="/us/copyright">
                  <a>版权保护</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <a
                  href="https://support.wujiebantu.com"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  帮助中心
                </a>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <a
                  href="https://data.wujiebantu.com/logopack-v2.1.zip"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  品牌素材
                </a>
              </StyledLinkItemLink>
            </StyledLinkItem>
            <StyledLinkItem>
              <div className="link-item-title">用户协议</div>
              <StyledLinkItemLink>
                <Link href="/us/privacy">
                  <a>隐私政策</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href="/us/user-agreement">
                  <a>用户协议</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href="/us/settled">
                  <a>创作者入驻协议</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href="/us/ai-artist">
                  <a>AI创作者入驻协议</a>
                </Link>
              </StyledLinkItemLink>
            </StyledLinkItem>
            <StyledLinkItem>
              <div className="link-item-title">版权协议</div>
              <StyledLinkItemLink>
                <Link href="/us/licensing">
                  <a>授权许可协议</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href="/us/ownership">
                  <a>所有权购买协议</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href="/us/adapt">
                  <a>改编权购买协议</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href={AiSkuTypeAgreement[SkuType.USE]}>
                  <a>AI作品授权许可协议</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href={AiSkuTypeAgreement[SkuType.BUYOUT]}>
                  <a>AI作品权利转让协议</a>
                </Link>
              </StyledLinkItemLink>
              <StyledLinkItemLink>
                <Link href={AiSkuTypeAgreement[SkuType.ADAPT]}>
                  <a>AI作品改编授权许可协议</a>
                </Link>
              </StyledLinkItemLink>
            </StyledLinkItem>
            <StyledLinkItem style={{ width: 96 }}>
              <div className="link-item-title">扫码下载APP</div>
              <div className="qrCode">
                <QRCode
                  value={'https://m.wujiebantu.com/download'}
                  size={90}
                  fgColor="#000000"
                  bgColor="#ffffff"
                ></QRCode>
              </div>
            </StyledLinkItem>
          </StyledLink>
        </div>
        <StyledCopyright>
          <StyledCopyrightText>
            Copyright © 2022 无界版图 版权所有{' '}
            <a
              href="https://beian.miit.gov.cn"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              浙ICP备2021000434号-3
            </a>
          </StyledCopyrightText>
          <StyledSocial>
            <StyledSocialItem>
              <Popover
                content={
                  <div
                    style={{
                      width: 150,
                      margin: '-6px -10px',
                      textAlign: 'center',
                    }}
                  >
                    <img
                      style={{ width: 150 }}
                      src="/images/contact-xiaohongshu.png"
                      alt=""
                    />
                    <div style={{ color: '#777E90' }}>
                      关注小红书
                      <br />
                      内容不迷路
                    </div>
                  </div>
                }
                placement="top"
              >
                <MyIcon
                  icon="xiaohongshu"
                  className="text-[24px] text-neutral-400"
                />
              </Popover>
            </StyledSocialItem>
            <StyledSocialItem>
              <a
                href="https://space.bilibili.com/1755959953"
                target="_blank"
                rel="nofollow noopener noreferrer"
              >
                <MyIcon
                  icon="bilibili"
                  className="text-[24px] text-neutral-400"
                />
              </a>
            </StyledSocialItem>
            <StyledSocialItem>
              <a
                href="https://www.zhihu.com/people/63-27-46-40"
                target="_blank"
                rel="nofollow noopener noreferrer"
              >
                <MyIcon icon="zhihu" className="text-[24px] text-neutral-400" />
              </a>
            </StyledSocialItem>
            <StyledSocialItem>
              <a
                href="https://v.douyin.com/jQxDwqw/"
                target="_blank"
                rel="nofollow noopener noreferrer"
              >
                <MyIcon
                  icon="tiktok"
                  className="text-[24px] text-neutral-400"
                />
              </a>
            </StyledSocialItem>
            <StyledSocialItem>
              <a
                href="https://weibo.com/u/6993253160"
                target="_blank"
                rel="nofollow noopener noreferrer"
              >
                <MyIcon
                  icon="weibo-filled"
                  className="text-[24px] text-neutral-400"
                />
              </a>
            </StyledSocialItem>
            <StyledSocialItem>
              <Popover
                content={
                  <div
                    style={{
                      width: 150,
                      margin: '-6px -10px',
                      textAlign: 'center',
                    }}
                  >
                    <img
                      style={{ width: 150 }}
                      src="/images/business-v2.jpg"
                      alt=""
                    />
                    <div style={{ color: '#777E90' }}>
                      添加平台微信
                      <br />
                      收获万象更新
                    </div>
                  </div>
                }
                placement="top"
              >
                <MyIcon
                  icon="weixin"
                  className="text-[24px] text-neutral-400"
                />
              </Popover>
            </StyledSocialItem>
            <StyledSocialItem>
              <Tooltip placement="top" title="邮箱：business@wujiebantu.com">
                <MyIcon icon="email" className="text-[24px] text-neutral-400" />
              </Tooltip>
            </StyledSocialItem>
          </StyledSocial>
        </StyledCopyright>
      </Container>
    </StyledFooter>
  )
})
